import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

// ============================================================
// MDeRCM SMART CHATBOT — NO API REQUIRED
// Keyword-based intelligent responses from website content
// Lead capture → Google Sheets
// ============================================================

const SHEETS_URL = 'https://script.google.com/macros/s/AKfycbybcWTRhyvT-CV1HpIznRKu4zqbFDJ3sEhcJX54JfAAqsH4taYIlGHGHt_LmlpTx6RsAA/exec';

// ══════════════════════════════════════════════════════════
// KNOWLEDGE BASE — Website content ke hisaab se answers
// ══════════════════════════════════════════════════════════
const KB = [
  // ── PRICING ──────────────────────────────────────────────
  {
    keys: ['pricing', 'price', 'cost', 'how much', 'charge', 'fee', 'rate', 'plan', 'package', 'kitna', 'paisa', 'affordable', 'cheap', 'expensive'],
    answer: `💲 **MDeRCM Pricing — Performance-Based Model**

• **% of collections only** — we earn when you earn
• **First 30 days FREE** — no credit card required
• No setup fees, no hidden charges, no surprise fees
• No long-term contracts required — month-to-month available
• Covers everything: coding, claims, AR, denials, reporting & support
• Pricing factors: practice size, claim volume, specialty, payer mix, EHR system
• Practice sizes: Small (1–5 providers), Medium, Large Multispecialty (5,000+ claims/mo)
• Annual savings: **$40,000–$150,000** for most practices
• Cost savings: **50–65%** vs in-house billing
• ROI achieved within **6–12 months**
• Volume-based discounts available
• Dedicated implementation support included

📖 See full pricing → [mdercm.com/pricing](/pricing)
🎁 Start free trial → [mdercm.com/free-trial](/free-trial)`,
  },
  // ── FREE TRIAL ───────────────────────────────────────────
  {
    keys: ['free trial', 'trial', 'free', 'start', 'begin', 'get started', 'demo', 'sign up', 'signup', 'onboard', 'try'],
    answer: `🎁 **MDeRCM Free Trial**

• **30 days completely FREE**
• No credit card required
• No setup fees
• Full access to all AI tools
• Dedicated onboarding support included
• Team follows up within **24 hours**

Start today → [mdercm.com/free-trial](/free-trial)`,
  },
  // ── DENIAL MANAGEMENT ───────────────────────────────────
  {
    keys: ['denial', 'denied', 'claim denied', 'rejection', 'rejected', 'appeal', 'denial management', 'claim denial', 'co code', 'denial code', 'unworked'],
    answer: `🛡️ **AI Denial Management**

• **70% reduction** in claim denials
• AI screens claims **before submission** — catches errors first
• **89% appeal success rate** | Industry avg: 50-60%
• Automated appeal letters with clinical documentation
• Root cause analysis prevents recurring denials
• **35–45% denial reduction**, **$50K–$150K annual recovery**
• Appeal turnaround: **24–48 hours**
• 80–85% of denials are preventable administrative errors
• 63% of denied claims are never reworked — we work ALL of them
• Handles all denial types: coding errors, missing info, medical necessity, auth issues, timely filing, duplicates, COB
• Normal denial rate: 10–15% industry avg → MDeRCM: **3–5%**

📖 AI Denial Management → [/healthcare/ai/denial-management](/healthcare/ai/denial-management)
📖 Reduce Denials Guide → [/reduce-claim-denials-healthcare-billing](/reduce-claim-denials-healthcare-billing)
📖 Denial Codes Reference → [/resources/denial-codes](/resources/denial-codes)`,
  },
  // ── ELIGIBILITY ──────────────────────────────────────────
  {
    keys: ['eligibility', 'eligibility check', 'insurance verification', 'verify insurance', 'benefits check', 'coverage check', 'payer', 'insurance check'],
    answer: `✅ **AI Eligibility Verification**

• **8 seconds** per patient (vs 10–15 min manual)
• **600+ payer** integrations — Medicare, Medicaid, all commercial
• Real-time: benefits, copay, deductible, coverage details
• **85% reduction** in eligibility-related denials
• **99%+ real-time validation reliability**
• Significant **cut in front-desk verification time**
• Works before every patient visit automatically

📖 Learn more → [/healthcare/ai/eligibility-check](/healthcare/ai/eligibility-check)
📖 Policy Status Verification → [/healthcare/ai/policy-status-verification](/healthcare/ai/policy-status-verification)`,
  },
  // ── PRIOR AUTHORIZATION ──────────────────────────────────
  {
    keys: ['prior auth', 'prior authorization', 'authorization', 'pre-auth', 'preauth', 'pa request', 'auth request', 'authorization denial', 'auth turnaround'],
    answer: `📋 **AI Prior Authorization**

• **75% less time** spent on authorization tasks
• **1.8–2.5 hour** turnaround (vs 16–24 hours manual)
• **93% first-pass** approval rate
• **Fewer denials** linked to prior authorization
• Auto-submission to all major payers
• Covers: inpatient, outpatient, specialty drugs, procedures, mental health services
• Tracks approval status across multiple payers
• Reduces treatment delays by **80%**
• Alerts before authorization expires — prevents mid-treatment lapses
• Concurrent review scheduling included

📖 AI Prior Authorization → [/healthcare/ai/prior-authorization](/healthcare/ai/prior-authorization)`,
  },
  // ── PAYMENT POSTING ──────────────────────────────────────
  {
    keys: ['payment posting', 'posting', 'era', 'eob', 'remittance', 'reconciliation', 'payment', 'cash posting', 'ocr', 'paper eob'],
    answer: `💳 **AI Payment Posting**

• **Faster than manual posting** — minutes vs 4–6 hours/day
• **99.9% accuracy** in payment allocation
• **95% reduction** in posting backlog
• AI-powered OCR captures paper EOBs with 99.9% accuracy
• Smart matching — payments to claims instantly, **95% less manual reconciliation**
• Handles Medicare, Medicaid, and all commercial insurers
• Real-time dashboards for payment trends
• Automatic balance reconciliation — flags discrepancies
• **Exception management** — partial payments, adjustments, denials
• Complete audit trail for compliance
• **Cut payment posting costs by up to 70%** through automation

📖 AI Payment Posting → [/healthcare/ai/payment-posting](/healthcare/ai/payment-posting)`,
  },
  // ── ACCOUNTS RECEIVABLE ──────────────────────────────────
  {
    keys: ['accounts receivable', 'ar', 'a/r', 'outstanding', 'collections', 'days in ar', 'aging', 'follow up', 'follow-up', 'cash flow', 'unpaid claims'],
    answer: `💵 **AI Accounts Receivable**

• Days in A/R: **16–24 days** (industry avg: 55–70 days)
• A/R over 90 days reduced to **under 5%**
• AI prioritizes follow-up queue automatically
• Automated payer portal checks & calls
• **40–55% revenue increase** reported by clients
• **20–30% faster payments** vs industry average
• $40K–$80K unlocked from delayed payments for independent physicians

📖 AI AR Management → [/healthcare/ai/accounts-receivable](/healthcare/ai/accounts-receivable)`,
  },
  // ── COMPLIANCE ───────────────────────────────────────────
  {
    keys: ['compliance', 'hipaa', 'oig', 'cms', 'audit', 'regulatory', 'violation', 'compliant', 'security', 'data security', 'encrypted', 'privacy', 'gdpr'],
    answer: `🔒 **AI Compliance Agent + Data Security**

• Continuous **HIPAA, OIG, CMS** monitoring 24/7
• Flags compliance risks before violations occur
• Complete audit trail for all billing activities
• Regular compliance reports generated automatically
• **SOC 2 certified** — your data is fully secure
• Robust encryption protocols + secure authentication
• ISO certifications maintained
• All communications fully encrypted per healthcare privacy regulations
• Privacy policy: [/privacy](/privacy)

📖 AI Compliance → [/healthcare/ai/compliance-agent](/healthcare/ai/compliance-agent)`,
  },
  // ── PATIENT INTAKE ───────────────────────────────────────
  {
    keys: ['patient intake', 'intake', 'patient registration', 'patient forms', 'patient reminder', 'patient payment', 'patient billing', 'multilingual', 'payment plan'],
    answer: `📱 **AI Patient Intake & Reminders**

• **Increase in on-time payments**
• **Boost in patient response rates**
• **Reduction in overdue balances**
• Seamlessly connects with EHR and billing systems in real-time
• Personalized messaging — multi-channel delivery
• Track payment rates, response times with detailed dashboards
• Fully encrypted, HIPAA-compliant communications
• **Multilingual support** available
• Customizable branded templates
• **Payment plan integration** for high-balance accounts
• Automated escalation — frees up staff time
• Two-way communication with patients
• Free up staff by eliminating manual call campaigns

📖 AI Patient Intake → [/healthcare/ai/patient-intake](/healthcare/ai/patient-intake)`,
  },
  // ── POLICY STATUS VERIFICATION ───────────────────────────
  {
    keys: ['policy status', 'policy verification', 'insurance lapse', 'lapsed coverage', 'coverage verification', 'policy check', 'insurance status'],
    answer: `🔎 **AI Policy Status Verification**

• **99%+ real-time validation reliability**
• Significant **cut in front-desk verification time**
• **Quicker than traditional methods**
• Catches lapsed coverage BEFORE service is rendered
• AI adapts to each payer — Medicare, Medicaid, private insurers
• Intelligent claim engine reviews each claim instantly

📖 Policy Status Verification → [/healthcare/ai/policy-status-verification](/healthcare/ai/policy-status-verification)`,
  },
  // ── MENTAL HEALTH ────────────────────────────────────────
  {
    keys: ['mental health', 'behavioral health', 'therapy', 'therapist', 'psychiatry', 'psychiatrist', 'counseling', 'counselor', 'lcsw', 'lpc', 'lmft', 'psychologist', 'psychology', 'behavioral', 'telehealth mental'],
    answer: `🧠 **Mental Health & Behavioral Health Billing**

• **95% automation** for mental health practices
• MHPAEA mental health parity law compliance
• 42 CFR Part 2 (substance abuse) confidentiality compliance
• MBHO carve-out detection — finds when BH is carved to separate payer
• Telehealth mental health billing (POS 02/10, Modifier 95/GT)
• LCSW, LPC, LMFT, psychiatrist billing
• CPT codes: 90791, 90834, 90837, 90847, 90853, add-ons 90833/90836/90838
• Prior authorization: reduces treatment delays **80%**
• Submit mental health claims with accurate CPT and DSM coding
• Automated utilization review — tracks authorized sessions, submits renewals, alerts before expiry
• Insurance coordination — primary & secondary mental health coverage
• **50% cost reduction**, **70% revenue increase**
• Clean claim rate: **97–98.5%**

📖 Mental Health Billing → [/MentalHealth](/MentalHealth)
📖 Complete 2026 Guide → [Mental Health & SUD RCM Guide](/blog/mental-health-substance-abuse-rcm-billing-guide-2026)
📖 Best BH Software 2026 → [Behavioral Health RCM Software](/blog/best-behavioral-health-rcm-software-companies-2026)`,
  },
  // ── SUBSTANCE ABUSE / SUD ────────────────────────────────
  {
    keys: ['substance abuse', 'sud', 'addiction', 'detox', 'rehab', 'mat', 'iop', 'php', 'residential', 'buprenorphine', 'methadone', 'naltrexone', 'asam', 'treatment center'],
    answer: `💊 **Substance Abuse (SUD) Billing**

• ASAM level-of-care billing (all 6 levels — outpatient through residential detox)
• MAT billing: buprenorphine, naltrexone, methadone (G-codes)
• IOP, PHP, residential, detox billing
• **42 CFR Part 2** compliance — consent management, disclosure tracking, audit trails
• Concurrent review management — auto-scheduling, documentation prep, extension management
• **70% denial reduction** for SUD claims
• Utilization management — tracks authorized sessions, submits renewals before expiry

📖 Mental Health & SUD Page → [/MentalHealth](/MentalHealth)
📖 Full SUD Billing Guide → [Mental Health & Substance Abuse RCM 2026](/blog/mental-health-substance-abuse-rcm-billing-guide-2026)`,
  },
  // ── DENTAL ───────────────────────────────────────────────
  {
    keys: ['dental', 'dentist', 'dental billing', 'cdt', 'dental insurance', 'teeth', 'orthodontics', 'oral'],
    answer: `🦷 **Dental Billing Services**

• CDT code expertise
• Dental insurance billing & claims management
• Predetermination management
• Dental-specific denial management
• All dental specialties covered including orthodontics
• Insurance verification for dental benefits

📖 Dental Billing → [/Dental](/Dental)`,
  },
  // ── HOSPITAL RCM ─────────────────────────────────────────
  {
    keys: ['hospital', 'hospital rcm', 'inpatient', 'ub-04', 'drg', 'facility billing', 'health system', 'hospital billing'],
    answer: `🏥 **Hospital Revenue Cycle Management**

• Full end-to-end hospital RCM
• UB-04 billing, DRG optimization
• Charge capture and revenue integrity
• Payer contract management
• Revenue increase: **40–55%** for hospital clients
• Inpatient + outpatient billing
• Revenue integrity monitoring

📖 Hospital RCM → [/hospital-revenue-cycle-management](/hospital-revenue-cycle-management)`,
  },
  // ── SMALL PRACTICES ──────────────────────────────────────
  {
    keys: ['small practice', 'solo', 'small clinic', '1-5 provider', 'small doctor', 'private practice', 'solo practice', 'small group'],
    answer: `👨‍⚕️ **RCM for Small Practices (1–5 Providers)**

**Problems we solve:**
• In-house billing costs **$40K–$60K/year per employee** (15–20% of total revenue)
• Limited coding expertise → 5–10% revenue lost to coding errors
• High denial rates: small practices avg **10–15%** vs our **3–5%**
• Delayed cash flow: claims sit 3–7 days, payments take 60–90 days
• Staff turnover costs **$10K–$30K** per incident
• Physicians waste **10–15 hours/week** on billing issues

**MDeRCM results for small practices:**
• **Save $40K–$60K** in salary costs
• Clean claim rate: **97–98.5%** (vs 78–84% small practice avg)
• Days in A/R: **16–24 days** (vs 52–68 days average)
• **63% of denied claims** that never get worked — we work ALL of them
• Certified CPC coders with 10+ years experience

📖 Small Practice Billing → [/medical-billing-outsourcing-small-practices](/medical-billing-outsourcing-small-practices)
📖 Small Practice Guide → [Best RCM for Small Practices 2026](/blog/best-rcm-software-small-medical-practices-2026)`,
  },
  // ── INDEPENDENT PHYSICIANS ───────────────────────────────
  {
    keys: ['independent physician', 'solo physician', 'physician billing', 'independent doctor', 'private doctor', 'physician practice', 'physician rcm'],
    answer: `👨‍⚕️ **RCM for Independent Physicians**

**Common problems we solve:**
• Wearing too many hats — billing gets only 2–3 hours rushed attention → **$3K–$8K lost monthly**
• In-house billing: **$45K–$55K/year** + 30% benefits + $10K software = **$60K–$80K** just to break even
• Coding errors cost **5–8% of potential revenue** annually
• **$40K–$80K constantly tied up** in delayed payments
• Denial rates: **12–18%** for independent docs vs **3–5%** with MDeRCM
• **70% of denied claims** never appealed — we appeal ALL of them
• Annual lost revenue from unworked denials: **$25K–$60K**

**MDeRCM results:**
• Revenue increase: **25–35%**
• **10+ hours saved weekly** for patient care
• **95%+ collection rate**
• **20–30% faster payments**

📖 Independent Physician RCM → [/revenue-cycle-management-independent-physicians](/revenue-cycle-management-independent-physicians)`,
  },
  // ── MULTISPECIALTY ───────────────────────────────────────
  {
    keys: ['multispecialty', 'multi specialty', 'multi-specialty', 'large practice', 'group practice', 'physician group', 'multiple specialties', 'multiple locations'],
    answer: `🏢 **Multispecialty Practice RCM**

• **25–35% revenue increase**
• **30–40% cost reduction**
• **99%+ clean claim rate**
• **20–30% faster payments**
• Specialty-specific coding teams — cardiology by cardiology-certified coders, orthopedics by orthopedic specialists
• **98%+ coding accuracy** across all specialties
• Unified technology platform → **reduce tech costs 30–40%**
• Comprehensive analytics — specialty-level AND practice-wide reporting
• Cross-specialty coordination — capture **100% of billable services**
• Denial management → reduce denials to **2–3%**
• Multi-location support — seamless multi-site operations
• Scales to new specialties/locations in **2–3 weeks**
• ROI: **4–6 months** for larger practices ($5M+)
• MIPS reporting, value-based care, ACO support included

📖 Multispecialty RCM → [/revenue-cycle-management-multispecialty-practices](/revenue-cycle-management-multispecialty-practices)`,
  },
  // ── MEDICAL BILLING OUTSOURCING ──────────────────────────
  {
    keys: ['outsource', 'outsourcing', 'why outsource', 'benefits of outsourcing', 'medical billing outsourcing', 'offshore billing', 'offshore'],
    answer: `📤 **Medical Billing Outsourcing Benefits**

• **30–50% cost reduction** — eliminate overhead of in-house staff, software, infrastructure
• **95%+ collection rate** — expert billers with persistent follow-up
• Payment cycles: **15–20 days** (vs 60 days in-house)
• Reduced denials with AI-powered validation
• Certified medical billing specialists — no recruitment or training costs
• Full HIPAA compliance, ISO certifications, data security
• Services: Claims Submission, Medical Coding (CPT/ICD-10), Insurance Verification, Reporting & Analytics
• **50–65% cost savings** vs maintaining in-house billing

📖 Medical Billing Outsourcing → [/medical-billing-outsourcing](/medical-billing-outsourcing)
📖 Outsourcing Benefits → [/benefits-medical-billing-outsourcing-clinics](/benefits-medical-billing-outsourcing-clinics)`,
  },
  // ── RCM CONSULTING ───────────────────────────────────────
  {
    keys: ['consulting', 'consultant', 'advisory', 'strategy', 'assessment', 'audit', 'review', 'rcm assessment', 'revenue optimization', 'kpi'],
    answer: `💼 **RCM Consulting Services**

Services include:
• **RCM Assessment & Analysis** — evaluate current processes, identify bottlenecks
• **Denial Management Strategy** — reduce denials, improve appeal success
• **Revenue Optimization** — coding, charge capture, collection improvements
• **Charge Capture Review, Underpayment Detection, Collection Strategies**
• **Technology Integration** — AI tools, automation, analytics
• **Performance Improvement** — KPIs, monitoring, continuous improvement
• **Staff Training & Development** — billing, coding, revenue cycle training

Results: **Average 35% improvement** in collection rates, **40% reduction in denials** for consulting clients
**20+ years** combined experience across hospitals, clinics, health systems

📖 RCM Consulting → [/revenue-cycle-consulting](/revenue-cycle-consulting)`,
  },
  // ── REDUCE CLAIM DENIALS ────────────────────────────────
  {
    keys: ['reduce denial', 'prevent denial', 'denial prevention', 'avoid denial', 'denial rate', 'denial reasons', 'common denial'],
    answer: `🚫 **Reduce Claim Denials — Common Causes & Solutions**

**Top denial reasons:**
• Wrong demographics / insurance info / dates of birth
• Duplicate claim submissions
• Procedure not in patient's benefit plan
• Claim filed after payer deadline (90–365 days)
• Wrong CPT codes, missing modifiers, unbundling
• Missing prior authorization
• CCI edits — services included in other procedures
• Documentation doesn't support procedure code

**Our 3-phase approach:**
1. **Rapid Recovery** — $15K–$50K recovered within first 30 days from existing denied claims
2. **Root Cause Elimination** — fix systematic errors
3. **Ongoing Optimization** — maintain 3–5% denial rate permanently

**Results:** 35–45% denial reduction | $50K–$150K annual recovery | 24–48 hr appeal turnaround | 75–85% appeal success

📖 Reduce Denials → [/reduce-claim-denials-healthcare-billing](/reduce-claim-denials-healthcare-billing)
📖 Denial Management → [/denial-management-services-medical-clinics](/denial-management-services-medical-clinics)`,
  },
  // ── GURGAON / INDIA ──────────────────────────────────────
  {
    keys: ['gurgaon', 'gurugram', 'india', 'location', 'office', 'where are you', 'based', 'headquarters', 'india based'],
    answer: `📍 **MDeRCM — Gurgaon, India**

• Headquartered in **Gurgaon (Gurugram)**, Haryana
• Serving **500+ US healthcare providers** across all 50 states
• US address: Five Greentree Centre, 525 Route 73 North, Suite 104
• 24/7 operations (India–US time zone advantage)
• **40–60% cost savings** vs US-based billing companies
• HIPAA compliant, SOC 2 certified
• CPC-certified coders with US payer expertise

**Leadership Team:**
• **Poonam Kapoor** — CEO, 20+ years global outsourcing, Quality Assurance & Process Optimization
• **Rishi Kapoor** — CTO/COO, 20+ years RCM expertise, EDI/Automation/Denial Management/Compliance

📖 About MDeRCM → [/company](/company)
📖 Gurgaon Guide → [Medical Billing Companies in Gurgaon](/blog/medical-billing-companies-gurgaon-india)`,
  },
  // ── TEAM / LEADERSHIP ────────────────────────────────────
  {
    keys: ['team', 'leadership', 'ceo', 'cto', 'founder', 'poonam', 'rishi', 'kapoor', 'management', 'who runs', 'about team'],
    answer: `👥 **MDeRCM Leadership Team**

**Poonam Kapoor — CEO**
• 20+ years leadership in global and US-based outsourcing
• Expertise: Quality Assurance, Process Optimization, Automation, Human Resources
• Focus: operational excellence and scalable team growth

**Rishi Kapoor — Chief Technology & Operations Officer**
• 20+ years deep domain RCM expertise
• Specializes in: EDI, Automation, Integration, Process Transition, Marketing, Sales, AR, Denial Management, Compliance
• Served in various roles at leading RCM firms

📖 Meet the team → [/company](/company)`,
  },
  // ── AI SERVICES ──────────────────────────────────────────
  {
    keys: ['ai', 'artificial intelligence', 'automation', 'automated', 'technology', 'how does ai', 'machine learning', 'ai powered', 'ai tools'],
    answer: `🤖 **MDeRCM AI Platform — Full Suite**

All services powered by AI automation:
• ✅ Eligibility: **8 sec/patient** → [/healthcare/ai/eligibility-check](/healthcare/ai/eligibility-check)
• 📋 Prior Auth: **1.8–2.5 hrs** → [/healthcare/ai/prior-authorization](/healthcare/ai/prior-authorization)
• 🛡️ Denial Management: **70% reduction** → [/healthcare/ai/denial-management](/healthcare/ai/denial-management)
• 💳 Payment Posting: **99.9% accuracy** → [/healthcare/ai/payment-posting](/healthcare/ai/payment-posting)
• 💵 Accounts Receivable: **16–24 day A/R** → [/healthcare/ai/accounts-receivable](/healthcare/ai/accounts-receivable)
• 🔒 Compliance Agent: **HIPAA/OIG/CMS** → [/healthcare/ai/compliance-agent](/healthcare/ai/compliance-agent)
• 📱 Patient Intake → [/healthcare/ai/patient-intake](/healthcare/ai/patient-intake)
• 🔎 Policy Verification → [/healthcare/ai/policy-status-verification](/healthcare/ai/policy-status-verification)
• 📄 Contract Repricing → [/healthcare/ai/InsuranceContract-Repricing](/healthcare/ai/InsuranceContract-Repricing)

Overall: **95% process automation** | **97–98.5%** clean claim rate

📖 All AI Services → [/healthcare/ai](/healthcare/ai)
📖 AI RCM 2026 Guide → [Best AI Healthcare RCM 2026](/blog/best-ai-healthcare-revenue-cycle-management-2026)`,
  },
  // ── CONTRACT REPRICING ───────────────────────────────────
  {
    keys: ['contract', 'repricing', 'underpayment', 'payer contract', 'contract analysis', 'underpaid', 'insurance contract'],
    answer: `📄 **Insurance Contract Repricing**

• Analyzes payer contracts for underpayments
• Identifies where payers pay **below contracted rates**
• Systematic recovery of underpaid claims
• Payer contract analysis and optimization
• Can increase contracted rates by **5–10%**

📖 Contract Repricing → [/healthcare/ai/InsuranceContract-Repricing](/healthcare/ai/InsuranceContract-Repricing)
📖 RCM Consulting → [/revenue-cycle-consulting](/revenue-cycle-consulting)`,
  },
  // ── TELEHEALTH ───────────────────────────────────────────
  {
    keys: ['telehealth', 'telemedicine', 'virtual visit', 'video visit', 'remote', 'online visit', 'virtual care', 'pos 02', 'modifier 95'],
    answer: `📱 **Telehealth Billing**

• Correct **POS codes** (02 vs 10) auto-applied per payer rules
• **Modifier 95/GT** applied automatically
• Payer-specific telehealth rules per claim
• Audio-only billing rules included
• Mental health telehealth specialty expertise
• Reduces telehealth-related denials significantly

📖 Mental Health Telehealth Billing → [/MentalHealth](/MentalHealth)
📖 Full Telehealth Guide → [Mental Health & SUD RCM Guide](/blog/mental-health-substance-abuse-rcm-billing-guide-2026)`,
  },
  // ── HOW IT WORKS ─────────────────────────────────────────
  {
    keys: ['how does it work', 'process', 'workflow', 'steps', 'how do you', 'explain', 'what happens', 'procedure', 'implementation', 'onboarding', 'setup'],
    answer: `⚙️ **How MDeRCM Works**

**Implementation Timeline:**
1. **Discovery & Assessment** — evaluate current RCM processes
2. **Analysis & Strategy** — data analysis, benchmarking, strategic roadmap
3. **Connect your EHR/PM** — 600+ integrations, typically **1–2 weeks setup**
4. **AI takes over** — eligibility → coding → claims → PA → denial management → payment posting
5. **Real-time dashboard** — track every KPI live

**Results timeline:**
• Immediate: cost savings from reduced staff/software
• 30 days: **$15K–$50K recovered** from existing denied claims
• 60–90 days: measurable denial rate improvement
• 6–12 months: **full ROI achieved**

📖 Why MDeRCM → [/why](/why)
📖 RCM Consulting → [/revenue-cycle-consulting](/revenue-cycle-consulting)
🎁 Start free → [/free-trial](/free-trial)`,
  },
  // ── EHR INTEGRATION ──────────────────────────────────────
  {
    keys: ['integration', 'ehr', 'emr', 'epic', 'athena', 'drchrono', 'eclinicalworks', 'practice fusion', 'kareo', 'connect', 'software', 'system', 'valant', 'therapynotes', 'netsmart', 'kipu'],
    answer: `🔗 **EHR & System Integrations**

• **600+ payer** integrations
• Works with all major EHR/PM systems:
  Epic, Athenahealth, eClinicalWorks, DrChrono,
  Kareo/Tebra, AdvancedMD, Valant, TherapyNotes,
  Netsmart, Kipu, Qualifacts CareLogic, and more
• API + HL7 FHIR connections
• Multiple EHRs simultaneously within one practice
• Platform aggregates data across systems for unified reporting
• Quick setup — typically **1–2 weeks** per specialty/location

📖 AI Services Overview → [/healthcare/ai](/healthcare/ai)`,
  },
  // ── RESOURCES ────────────────────────────────────────────
  {
    keys: ['denial code', 'co code', 'co denial', 'contractual obligation', 'remark code', 'adjustment code', 'denial codes list'],
    answer: `📋 **CO Denial Codes Reference**

Gain clear insights into **Contractual Obligation (CO) denial codes**:
• Why they're issued and what they represent
• How they affect claim reimbursements
• Root causes with real-world scenarios
• Proven strategies to prevent revenue loss
• Faster claim resolution techniques

📖 View Full Denial Codes List → [/resources/denial-codes](/resources/denial-codes)
📖 All Resources → [/resources](/resources)`,
  },
  // ── MIPS / VALUE-BASED ───────────────────────────────────
  {
    keys: ['mips', 'macra', 'value based', 'value-based', 'aco', 'quality measure', 'population health', 'merit-based'],
    answer: `📊 **MIPS, MACRA & Value-Based Care**

• Comprehensive support for value-based contracts
• ACO participation support
• **MIPS reporting** — all quality measures tracked
• Population health management data aggregation
• Optimize value-based revenue streams
• E/M coding optimization for MIPS compliance

📖 Multispecialty RCM → [/revenue-cycle-management-multispecialty-practices](/revenue-cycle-management-multispecialty-practices)
📖 RCM Consulting → [/revenue-cycle-consulting](/revenue-cycle-consulting)`,
  },
  // ── COST REDUCTION ───────────────────────────────────────
  {
    keys: ['cost reduction', 'reduce cost', 'save money', 'operational cost', 'overhead', 'cut cost', 'lower cost', 'roi', 'return on investment', 'savings'],
    answer: `💰 **Cost Reduction with MDeRCM**

• **40–60% operational cost reduction**
• AI automation reduces labor costs **70–80%**
• Eliminate: in-house billing staff ($40K–$60K/yr each), software ($5K–$15K/yr), training, overhead
• Cloud platform saves **$35K–$85K annually** vs on-premise
• $300K–$500K annual savings for a $5M revenue practice
• Denial prevention saves **$125K–$195K/year**
• Hidden benefit: 15–25% better collections = **$375K–$625K additional revenue** for $5M practice
• Payment cycle: 60 days → **15–20 days** = massive cash flow improvement

📖 Cost-Effective RCM Guide → [/blog/cost-effective-revenue-cycle-management-reduce-operational-costs](/blog/cost-effective-revenue-cycle-management-reduce-operational-costs)
📖 See pricing → [/pricing](/pricing)`,
  },
  // ── BLOG / RESOURCES ─────────────────────────────────────
  {
    keys: ['blog', 'article', 'guide', 'read', 'resource', 'learn', 'knowledge', 'white paper', 'case study'],
    answer: `📚 **MDeRCM Blog & Resources**

Latest guides:
• [Best AI Healthcare RCM 2026](/blog/best-ai-healthcare-revenue-cycle-management-2026)
• [Mental Health & SUD RCM Guide](/blog/mental-health-substance-abuse-rcm-billing-guide-2026)
• [Best Behavioral Health RCM Software](/blog/best-behavioral-health-rcm-software-companies-2026)
• [Cost-Effective RCM Guide](/blog/cost-effective-revenue-cycle-management-reduce-operational-costs)
• [Best RCM for Small Practices](/blog/best-rcm-software-small-medical-practices-2026)
• [Selecting Best RCM Companies 2026](/blog/selecting-best-rcm-companies-2026)
• [Medical Billing Companies Gurgaon](/blog/medical-billing-companies-gurgaon-india)
• [Denial Codes Reference](/resources/denial-codes)

📖 All blogs → [/blog](/blog)
📖 All resources → [/resources](/resources)`,
  },
  // ── CONTACT / SUPPORT ────────────────────────────────────
  {
    keys: ['contact', 'talk', 'speak', 'call', 'reach', 'sales', 'team', 'support', 'help', 'human', 'person', 'agent', 'email', 'phone number'],
    answer: `📞 **Contact MDeRCM**

• 🎁 **Start Free Trial** (best way): [mdercm.com/free-trial](/free-trial)
• 💲 **View Pricing**: [mdercm.com/pricing](/pricing)
• 🏢 **About Us & Team**: [mdercm.com/company](/company)
• 💼 **Careers**: [mdercm.com/careers](/careers)
• 📅 **Events**: [mdercm.com/events](/events)

Our team reviews all free trial requests within **24 hours** and will reach out to schedule your onboarding call. US office: Five Greentree Centre, 525 Route 73 North, Suite 104.`,
  },

  // ── DENTAL BILLING ──────────────────────────────────────
  {
    keys: ['dental', 'dentist', 'dental billing', 'cdt', 'dental insurance', 'teeth', 'orthodontics', 'oral', 'predetermination'],
    answer: `🦷 **Dental Practice Revenue Cycle Management**

**Common dental billing challenges we solve:**
• Insurance verification delays → **30–40% of claims denied** due to eligibility issues
• Manual verification: **15–20 min/patient** → MDeRCM: **8 seconds**
• **15–25% error rate** in manual claims submission → we reduce to under 2%
• Inefficient AR follow-up → payment cycles **60–90 days** → we reduce to **15–20 days**
• Manual reconciliation consuming staff hours
• Lack of transparent cost estimates upfront

**MDeRCM Dental Solutions:**
• CDT code expertise — all dental specialties
• Real-time dental insurance verification (600+ payers)
• Predetermination management
• Dental-specific denial management
• AR follow-up automation
• Patient billing & payment plans

📖 Dental Billing → [/Dental](/Dental)
🎁 Free Trial → [/free-trial](/free-trial)`,
  },
  // ── WHY MDERCM ──────────────────────────────────────────
  {
    keys: ['why mdercm', 'why choose', 'why you', 'what makes you different', 'benefits', 'advantage', 'compare', 'vs', 'better than', 'best rcm', 'trusted'],
    answer: `⭐ **Why Choose MDeRCM?**

**Key differentiators:**
• **Minimizes costs 30–50%** through AI automation of repetitive tasks
• **Boosts workforce efficiency** — team focuses on patient care, not billing
• **Enhances operational speed & accuracy by 40%+** across all RCM operations
• **Cuts manual labor 40–60%** through intelligent automation
• HIPAA-Compliant Data Security — PHI, PII, PCI automatic safeguards
• Robust encryption + secure authentication
• **500+ healthcare providers** currently served
• US-based dedicated team + Gurgaon 24/7 operations
• Medical Billing Outsourcing + AI + RCM Consulting — all in one

**Compliance standards maintained:**
• HIPAA, OIG, CMS, SOC 2, ISO certifications

**Results clients report:**
• 97–98.5% clean claim rate
• 70% denial reduction
• 40–55% revenue increase
• 50–65% cost savings
• Days in A/R: 16–24 days

📖 Why MDeRCM → [/why](/why)
📖 About Us → [/company](/company)`,
  },

  // ── CLAIMS PROCESSING ───────────────────────────────────
  {
    keys: ['claims processing', 'claim tracking', 'claim status', 'claim monitoring', 'claim submission', 'claim follow up', 'claim tracker', 'manual follow up', 'claim rejected'],
    answer: `📡 **AI Claims Processing & Status Tracking**

• **88% reduction** in manual follow-ups for authorizations
• **18x improvement** in authorization approval speed
• **75% decrease** in authorization-related claim rejections
• AI syncs with payer portals to track claim progress **live**
• Decodes complex payer status messages into clear, actionable steps
• Claims stuck in review or at risk of denial **flagged early**
• AI sorts claims by urgency, value, or payer rules — priority-based work queues
• Automatic resubmission: rejected claims routed with suggested fixes + required docs
• Unified dashboard — all payers in one place, no system switching
• **24/7 intelligent monitoring** — catches issues even outside business hours

📖 AI Claims Processing → [/healthcare/ai/claims-processing](/healthcare/ai/claims-processing)`,
  },

  // ── ACCOUNTS RECEIVABLE (DEEP) ───────────────────────────
  {
    keys: ['accounts receivable', 'ar management', 'a/r', 'aging accounts', 'outstanding balances', 'collections', 'cash flow', 'days in ar', 'payment delay', 'follow up'],
    answer: `💵 **AI Accounts Receivable Management**

• **Accelerate collections by 70%** — reduce A/R from 45+ days to **under 30 days**
• **Improve cash flow by 40%** through faster collections
• **Recover 30% more** from aged accounts through intelligent prioritization
• Smart Aging Analysis — AI predicts payment likelihood & identifies at-risk accounts
• Machine learning analyzes payment patterns & payer behavior
• AI continuously monitors accounts, flags aging accounts requiring immediate attention
• Automated Follow-ups — tracks responses, manages disputes, documents all activities
• Payment Plan Management for high-balance patients
• Predictive Analytics — root cause analysis of delayed payments
• Real-Time Dashboards — complete A/R visibility

📖 AI Accounts Receivable → [/healthcare/ai/accounts-receivable](/healthcare/ai/accounts-receivable)`,
  },

  // ── HOSPITAL RCM (DEEP) ──────────────────────────────────
  {
    keys: ['hospital', 'hospital rcm', 'hospital billing', 'inpatient', 'ub-04', 'drg', 'hospital claims', 'facility billing', '100 hospitals'],
    answer: `🏥 **Hospital Revenue Cycle Management**

**Common hospital challenges we solve:**
• 15–20% of hospital claims denied on first submission
• Average **45–60 days** from service to payment → we reduce to **15–20 days**
• Up to **5% revenue lost** due to coding errors
• High turnover and training costs for billing teams
• No real-time insights into revenue cycle performance

**MDeRCM Hospital Solutions:**
• **35–45% reduction** in claim denials
• **Lower operational costs** significantly
• **First-pass claim rate** dramatically improved
• AI-driven claim scrubbing — catches errors before submission, **reduces denials 40%**
• Pre-Authorization Management
• Real-Time Analytics dashboard
• Hospital-specific expertise — UB-04, DRG optimization
• Compliance & Security — HIPAA, SOC 2
• Trusted by **100+ hospitals** across USA

📖 Hospital RCM → [/hospital-revenue-cycle-management](/hospital-revenue-cycle-management)`,
  },

  // ── COMPLIANCE (DEEP) ────────────────────────────────────
  {
    keys: ['compliance', 'hipaa', 'iso', 'soc 2', 'security', 'encryption', 'audit', 'oig', 'cms', 'regulatory', 'certified', 'accreditation'],
    answer: `🔒 **MDeRCM Compliance & Security Standards**

**Certifications:**
• ✅ **ISO 9001:2015** — Quality Management System (valid until Feb 20, 2027)
• ✅ **SOC 2 Type II** — Five trust service principles, extended operational audit
• ✅ **HIPAA** — Privacy & Security Rules fully compliant
• ✅ QRO (Quality Research Organization) certified

**Security measures:**
• End-to-End Encryption (at rest & in transit)
• Multi-Factor Authentication
• Intrusion Detection System
• Disaster Recovery + regular Data Backup
• Audit Log Retention
• Session Timeout + Password Complexity controls
• PHI, PII, PCI automatic safeguards

**Compliance Agent features:**
• **Save 80% of time** on manual compliance checks
• **Reduce compliance errors significantly**
• Continuous 24/7 audit readiness monitoring
• Credentialing tracking — tracks expiration dates, 99.9% compliance accuracy
• Automated HIPAA training & awareness programs

📖 Compliance Standards → [/compliance](/compliance)
📖 AI Compliance Agent → [/healthcare/ai/compliance-agent](/healthcare/ai/compliance-agent)`,
  },

  // ── DENIAL MANAGEMENT (DEEP) ─────────────────────────────
  {
    keys: ['denial management', 'denial recovery', 'appeal', 'denied claim', 'denial rate', 'denial audit', 'claim denial', 'denial prevention', 'denial cost'],
    answer: `🛡️ **AI Denial Management — Deep Dive**

**The real cost of denials for clinics:**
• Direct revenue loss: **$45K–$135K annually**
• Staff time working denials: **$8K–$15K annually**
• Cash flow disruption: **$30K–$80K tied up**
• Staff turnover from frustration: **$15K–$30K per person**
• Missed prevention opportunities: **$20K–$50K annually**

**MDeRCM AI Denial Management features:**
• Automatic Detection + Root Cause Analysis
• Prioritization & Routing by value/urgency
• **Boost in First-Pass Recovery** — 40% increase in clean claim rate
• Intelligent Appeal Generation with clinical documentation
• Automated Resubmission workflows
• Reason Code Library — extensive database of denial codes + resolution strategies
• Timely Filing Tracking — never miss a deadline
• Predictive Prevention — catches errors pre-submission
• Performance Benchmarking vs industry standards
• Work Queue Management for billing teams
• Multi-Payer Integration

**Our 4-Step Proven Process:**
1. Denial Audit (free) — discover exact revenue being lost
2. Root cause identification & pattern analysis
3. Systematic appeal & resubmission
4. Prevention protocols to stop recurring denials

📖 AI Denial Management → [/healthcare/ai/denial-management](/healthcare/ai/denial-management)
📖 Denial Management for Clinics → [/denial-management-services-medical-clinics](/denial-management-services-medical-clinics)
🆓 Free Denial Audit → [/free-trial](/free-trial)`,
  },

  // ── INSURANCE CONTRACT REPRICING (DEEP) ─────────────────
  {
    keys: ['contract repricing', 'insurance contract', 'underpayment', 'payer contract', 'contract analysis', 'contract errors', 'pricing errors', 'contract compliance'],
    answer: `📄 **Insurance Contract Repricing**

• **Reduction in pricing errors** — AI scans contracts for errors, inconsistencies, outdated clauses
• **Faster contract review cycles** — up to **10x faster** contract processing
• **Increase in compliance accuracy**
• Adjusts prices continuously based on **real-time market conditions** + competitor data
• Provides actionable insights into market trends and competitor actions
• Ensures accurate contract pricing — maximize reimbursements, minimize revenue leakage
• Verify patient eligibility in real-time integrated with repricing
• Automated verification of insurance policy status for accurate billing

📖 Insurance Contract Repricing → [/healthcare/ai/InsuranceContract-Repricing](/healthcare/ai/InsuranceContract-Repricing)`,
  },

  // ── BENEFITS OF OUTSOURCING (DEEP) ──────────────────────
  {
    keys: ['benefits outsourcing', 'why outsource', 'outsource billing', 'outsourcing benefits', 'in-house vs outsource', 'should i outsource', 'outsourcing advantages'],
    answer: `📤 **15 Benefits of Medical Billing Outsourcing**

Top benefits:
1. **Revenue Increase 20–30%**
2. **Reduce Costs 40–50%**
3. **Save 15–20 hours weekly**
4. **Eliminate staffing headaches** — no hiring/training/turnover
5. **Reduce Denial Rates 40–50%**
6. **Faster Collections 25–35%**
7. Access expert specialization
8. Superior technology & analytics
9. **Scalability** without hiring pain
10. Enhanced compliance & security
11. Consistent performance
12. Better patient experience
13. **Transparency & reporting** — real-time dashboards
14. Focus on core competency — patient care
15. Predictable financial performance

**Real fact:** Less than 2% of practices truly operate in-house billing more efficiently than professional billing companies. Most discover **15–25% revenue leakage** when assessed.

**Backed by data from 1,000+ practices nationwide**

📖 All Benefits → [/benefits-medical-billing-outsourcing-clinics](/benefits-medical-billing-outsourcing-clinics)
📖 Pricing → [/pricing](/pricing)`,
  },

  // ── COMPANY / ABOUT (DEEP) ───────────────────────────────
  {
    keys: ['about mdercm', 'company', 'about us', 'who are you', 'mdercm company', 'about company', 'mdercm solutions', 'company info'],
    answer: `🏢 **About MDeRCM**

• One of the **best revenue cycle management companies** in the USA
• Proprietary AI platform automates **80% of routine RCM tasks**
• Reduces costs **30–40%** while improving accuracy to **99.9%**
• Combines machine learning, NLP, and predictive analytics
• **500+ US healthcare providers** served across all 50 states
• 24/7 support — dedicated account managers, real-time dashboards, monthly reviews

**US Office — MDERCM Solutions LLC:**
• Five Greentree Centre, 525 Route 73 North, Suite 104, Marlton, NJ 08053
• Phone: **+1 (510) 356-6069**
• Authorized Representatives: Poonam Kapoor & Rishi Kapoor

**India Office — MDeRCM:**
• DLF Forum, Cybercity, Phase III, Gurugram, Haryana 122002
• Phone: **+91-9311250055**

**Our RCM Approach (4 phases):**
1. **Assessment** — identify revenue leakage ($500K–$2M annually for larger practices)
2. **Strategy** — 60/90/180-day milestones + KPI targets
3. **Transition** — 30–45 day smooth go-live, zero cash flow disruption
4. **Optimization** — ongoing AI model refinement + monthly reviews

📖 About Us → [/company](/company)
📖 Why MDeRCM → [/why](/why)`,
  },

  // ── GLOBAL LOCATIONS / CONTACT ───────────────────────────
  {
    keys: ['address', 'phone number', 'contact number', 'us office', 'india office', 'new jersey', 'marlton', 'gurugram', 'call us', 'email us', 'where is office'],
    answer: `📍 **MDeRCM Office Locations**

**🇺🇸 US Office — MDERCM Solutions LLC**
• Five Greentree Centre, 525 Route 73 North, Suite 104
• Marlton, NJ 08053, USA
• Phone: **+1 (510) 356-6069**
• Representatives: Poonam Kapoor & Rishi Kapoor

**🇮🇳 India Office — MDeRCM**
• DLF Forum, Cybercity, Phase III
• Gurugram, Haryana 122002, India
• Phone: **+91-9311250055**

🎁 Best way to start: [Free Trial → /free-trial](/free-trial)
📖 View on map → [/company](/company)`,
  },

  // ── FAQ (DEEP) ───────────────────────────────────────────
  {
    keys: ['faq', 'question', 'how long', 'setup time', 'how does it work', 'integration', 'ehr compatible', 'credentialing', 'custom reports', 'report', 'dashboard'],
    answer: `❓ **Frequently Asked Questions**

**Setup & Integration:**
• Go-live time: **1–2 weeks** typically
• Works with Epic, Athena, eCW, DrChrono, Kareo + most EMR/EHR via APIs, HL7, or file-based automation
• Handles multiple EHRs simultaneously

**Performance:**
• A/R days reduced **40–50%** with AI AR Agent
• Claims Processing: AI CPT/ICD checks reduce denials, improve first-pass **35–40%**
• Credentialing Agent: 99.9% compliance accuracy, tracks all expiration dates

**Reporting & Support:**
• Custom reports: claims status, denials trends, AR aging, financial KPIs
• Real-time dashboards for all metrics
• **24/7 support** — live chat, phone, email + AI built into dashboard

**Who we serve:**
• Solo providers to large hospital systems
• All specialties: hospitals, physician practices, diagnostic labs, dental, mental health
• All 50 US states

📖 All FAQs → [/healthcare/ai](/healthcare/ai)
🎁 Get started → [/free-trial](/free-trial)`,
  },

  // ── EVENTS ──────────────────────────────────────────────
  {
    keys: ['events', 'annual event', 'company event', 'celebration', 'mdercm event', 'team event'],
    answer: `🎉 **MDeRCM Events**

• MDeRCM hosts **Annual Events** celebrating innovation in healthcare RCM
• Latest: **Annual Event 2025** — celebrating success and milestones
• Photo gallery available on our events page

📖 View Events & Gallery → [/events](/events)`,
  },

  // ── CO DENIAL CODES (DEEP) ──────────────────────────────
  {
    keys: ['co-1', 'co-2', 'co-4', 'co-11', 'co-15', 'co-16', 'co-18', 'co-22', 'co-23', 'co-27', 'co-29', 'co-45', 'co-50', 'co-97', 'co denial code', 'denial code list', 'co code'],
    answer: `📋 **Common CO Denial Codes & Fixes**

• **CO-1** — Deductible not met → Bill patient for their deductible
• **CO-2** — Coinsurance amount → Patient responsibility, bill patient
• **CO-4** — Procedure/modifier inconsistency → Review CPT/HCPCS modifier rules
• **CO-11** — Diagnosis inconsistent with procedure → Update diagnosis code
• **CO-15** — No prior authorization → Get retro auth or educate staff
• **CO-16** — Missing/incorrect claim info → Review and resubmit with required info
• **CO-18** — Duplicate claim → Only submit once unless correction needed
• **CO-22** — Exceeds benefit maximum → Check plan limits, bill patient
• **CO-23** — Service not covered / exclusion → Bill patient directly
• **CO-27** — Insurance coverage expired → Verify active coverage with patient
• **CO-29** — Timely filing limit exceeded → Appeal with documentation
• **CO-45** — Exceeds fee schedule → Adjust to payer allowable, write off rest
• **CO-50** — Not medically necessary → Submit medical records for appeal
• **CO-97** — Bundled procedure → Check bundling rules, unbundle if appropriate

📖 Full Denial Codes List → [/resources/denial-codes](/resources/denial-codes)
📖 Denial Management → [/healthcare/ai/denial-management](/healthcare/ai/denial-management)`,
  },

  // ── AI TECHNOLOGY DEEP DIVE ──────────────────────────────
  {
    keys: ['nlp', 'natural language processing', 'machine learning', 'rpa', 'robotic process', 'ai technology', 'how does ai work', 'ai platform', 'predictive', 'automation technology'],
    answer: `🤖 **MDeRCM AI Technology — Deep Dive**

**Core AI Technologies:**
• **NLP (Natural Language Processing)** — reads clinical documentation, auto-generates CPT/ICD-10/HCPCS codes with **99%+ accuracy** vs 94% manual coding
• **Predictive Denial Analytics** — ML models trained on **50M+ claim records** predict denial probability before submission
• **RPA (Robotic Process Automation)** — checks eligibility across **600+ payer portals** in **8 seconds** (vs 12–15 min manual)
• **AI Prior Authorization** — submits, tracks, approves — cutting PA time from **16 hours to under 2 hours**
• **AI Patient Intake** — captures demographic data at registration, eliminating errors that cause **61% of front-end denials**
• **Smart AR Prioritization** — ML analyzes payment patterns, payer behavior, claims — increases collection rates **12–18%**
• **Auto ERA/EOB matching** — posts **1,000+ transactions/hour** (vs 3–5 min each manually)
• **Predictive Revenue Analytics** — forecasts cash flow **30–60 days in advance** with **95% accuracy**

**AI Implementation Timeline:**
1. **Week 1–2:** Discovery & Baseline Assessment
2. **Week 3–6:** System Integration & Configuration
3. **Week 7–12:** Go-Live & Optimization
4. **Month 4+:** Continuous Improvement

**Result:** 85% of routine billing tasks handled by AI, **99.9% accuracy**, labor costs reduced **60–70%**

📖 AI Healthcare RCM 2026 → [/blog/best-ai-healthcare-revenue-cycle-management-2026](/blog/best-ai-healthcare-revenue-cycle-management-2026)
📖 All AI Services → [/healthcare/ai](/healthcare/ai)`,
  },

  // ── COST REDUCTION — DEEP STATS ─────────────────────────
  {
    keys: ['cost reduction', 'reduce cost', 'operational cost', 'how much save', 'save money', 'roi', 'return on investment', 'cost breakdown', 'cost comparison', 'annual savings'],
    answer: `💰 **Cost Reduction with AI-Powered RCM — Real Numbers**

**The hidden cost of traditional RCM ($5M practice):**
• In-house billing staff (3–5 FTEs): **$200K–$400K/year**
• Coding errors cause **30% of claim denials**
• Average denial rate 2026: **13%** (up from 9% in 2016)
• Every denied claim costs **$25–$117** to rework
• Manual eligibility: **$8–$12/patient** vs AI: **cents**
• Disconnected software systems: **$10K–$50K/year**
• HIPAA violations: **$100–$50,000/incident**

**AI vs Traditional — Annual Savings ($5M practice):**
• Staff cost savings: **$125K–$250K/year**
• Better collections (15–25% more): **+$375K–$625K**
• A/R freed up (45–60 days → 25–30 days): **$275K–$550K in working capital**
• Extra collections from AI follow-up: **+$100K–$300K annually**
• Patient retention improvement (15–25%): **+$750K–$1M/year**
• Business intelligence revenue gain: **8–15% annually**

**AI pricing: 4–7% of collections** vs in-house 12–18% cost-to-collect

**Real Case Study (12-provider Texas clinic):**
• Initial clean claims rate: 72% → **82% (Month 1)**
• Days in A/R: **52 → 35 days**
• Total annual savings: **$300K–$500K**

📖 Cost-Effective RCM Guide → [/blog/cost-effective-revenue-cycle-management-reduce-operational-costs](/blog/cost-effective-revenue-cycle-management-reduce-operational-costs)
📖 Reducing Costs with AI → [/blog/reducing-healthcare-costs-ai-rcm](/blog/reducing-healthcare-costs-ai-rcm)`,
  },

  // ── MAXIMIZING RETURNS / HOSPITAL ROI ───────────────────
  {
    keys: ['maximize revenue', 'maximizing returns', 'revenue leakage', 'revenue optimization', 'increase collections', 'hospital revenue', 'revenue improvement', 'financial impact'],
    answer: `📈 **Maximizing Healthcare Revenue Returns**

**Common revenue leakage points:**
• Hospitals lose **10–15%** of potential revenue to preventable billing errors
• Undercoding: **$30K–$50K lost annually per provider**
• Average 45–60 day collection cycle tying up millions

**MDeRCM Revenue Impact (large hospital example):**
• First-pass resolution rate improvement → **saves $1.2–1.8M in denial rework**
• Days in A/R: 50 → 28 days → **frees $6–8M in working capital**
• Additional collections: **+$5M annually**
• Operational expense savings: **$3M/year**
• **Total financial impact: $15–18 million improved annual performance**

**AI-specific gains:**
• Denial rate: 18% → **under 5%** with pre-submission AI screening
• 60–75% of denials **overturned on appeal**
• AI coding captures: **+$30K–$50K per provider/year**
• Cash flow predicted **30–60 days ahead** with 95% accuracy
• Labor cost reduction: **60–70%** while accuracy improves to 99.9%

📖 Maximizing Healthcare Returns → [/blog/maximizing-healthcare-returns](/blog/maximizing-healthcare-returns)
📖 Hospital RCM → [/hospital-revenue-cycle-management](/hospital-revenue-cycle-management)`,
  },

  // ── RCM COMPANIES COMPARISON ─────────────────────────────
  {
    keys: ['best rcm company', 'rcm companies', 'compare rcm', 'top rcm companies', 'rcm comparison', 'which company', 'best billing company', 'rcm provider', 'mdercm vs'],
    answer: `🏆 **Choosing the Best RCM Company — How to Evaluate**

**Top 10 criteria:**
1. **First-Pass Claim Acceptance Rate** — top companies: **95%+**; MDeRCM: **97–98.5%**
2. **Days in AR** — elite providers: under **30 days**; industry avg: 45–55 days
3. Technology stack & AI automation
4. Specialty-specific expertise
5. Denial management capabilities
6. Compliance & security (HIPAA, SOC 2, ISO)
7. Transparency & real-time reporting
8. Client support & communication
9. Scalability & growth support
10. Pricing structure — look for **4–8% of collections**, no hidden fees

**RCM Company Types:**
• Large legacy firms → Large hospitals, high cost, slow implementation
• AI-first companies (like MDeRCM) → All sizes, modern AI, rapid implementation, competitive ROI

**MDeRCM vs Competitors (small practices):**
• MDeRCM: **97–98.5%** clean claim rate | Kareo: 88–92% | AdvancedMD: 89–93% | DrChrono: 85–90%
• MDeRCM pricing: **4–6% of collections** (highest ROI)
• MDeRCM: Full AI suite + dedicated BH expertise

**Real client result:**
"Switching to MDeRCM increased our collections by **23% in 6 months** while reducing billing costs by **38%**. Their AI denial management reduced our denial rate from 18% to just **4%**."

📖 Best RCM Companies Guide → [/blog/best-rcm-companies-healthcare-providers](/blog/best-rcm-companies-healthcare-providers)
📖 Why MDeRCM → [/why](/why)`,
  },

  // ── BEHAVIORAL HEALTH RCM SOFTWARE ──────────────────────
  {
    keys: ['behavioral health software', 'bh software', 'therapy software', 'mental health software', 'rcm software', 'valant', 'kipu', 'therapynotes', 'netsmart', 'best software'],
    answer: `🧠 **Best Behavioral Health RCM Software 2026**

**BH-specific features to look for:**
• 42 CFR Part 2 Compliance
• MHPAEA Parity Enforcement
• Prior Auth Management for BH
• MBHO Carve-Out Detection
• AI Denial Prevention with BH payer edits
• Real-Time Analytics Dashboard

**BH RCM Software Comparison 2026:**
| Platform | BH Focus | Clean Claim Rate | Pricing |
|---|---|---|---|
| **MDeRCM** | ✅ Dedicated BH + Full AI | **97–98.5%** | % of collections |
| Valant | ✅ BH focused | 90–94% | Monthly SaaS |
| Kipu | ✅ SUD focused | 88–92% | Monthly SaaS |
| TherapyNotes | ✅ Therapy only | 82–87% | Monthly SaaS |
| Netsmart | ✅ BH focused | 90–94% | Enterprise |
| Kareo/Tebra | ⚠️ General+BH | 88–92% | Monthly SaaS |

**Pricing models:**
• % of Collections (4–8%): Aligned incentives — best ROI
• Monthly SaaS ($300–$2,500/mo): Predictable, fixed cost
• Per Claim ($2–$8/claim): Pay per use

**Real BH Practice Result:**
• Billing staff: 2.5 FTEs → 0.6 FTEs
• Parity violation recovery: significant revenue
• Days in A/R: **58 → 19 days**
• Cash flow improvement: **+$210,000**
• Total annual impact: **$454,000+**

📖 BH RCM Software 2026 → [/blog/best-behavioral-health-rcm-software-companies-2026](/blog/best-behavioral-health-rcm-software-companies-2026)`,
  },

  // ── SMALL PRACTICES COMPARISON ───────────────────────────
  {
    keys: ['small practice software', 'rcm for small practice', 'best for small', 'solo doctor software', 'small group billing', 'practice comparison', 'kareo', 'advancedmd', 'drchrono', 'collaboratemd'],
    answer: `👨‍⚕️ **Best RCM Solutions for Small Practices 2026**

**Small Practice Performance Benchmarks:**
| Metric | Industry Avg | MDeRCM |
|---|---|---|
| Clean Claim Rate | 78–84% | **97–98.5%** |
| Denial Rate | 16–22% | **3–6%** |
| Days in A/R | 52–68 days | **16–24 days** |
| Net Collection Rate | 71–79% | **93–97%** |
| Cost to Collect | 12–18% | **3–5%** |

**Platform Comparison (small practices):**
• **MDeRCM** — Full outsourced RCM, 97–98.5%, 4–6% of collections ✅ Best ROI
• Kareo/Tebra — SaaS+Billing, 88–92%, $200–600/mo, solo physicians
• AdvancedMD — SaaS+Billing, 89–93%, $400–800/mo, small groups
• DrChrono — EHR+Billing, 85–90%, $200–500/mo, solo practices
• CollaborateMD — PM+Billing, 87–91%, $300–600/mo, small clinics

**Real Small Practice Case Study:**
• Eliminated 2 billing FTEs → **$100K+ saved/year**
• Days in A/R: **61 → 20 days**
• Total annual impact: **$345,000+**

📖 Best RCM for Small Practices 2026 → [/blog/best-rcm-software-small-medical-practices-2026](/blog/best-rcm-software-small-medical-practices-2026)
📖 Small Practice Billing → [/medical-billing-outsourcing-small-practices](/medical-billing-outsourcing-small-practices)`,
  },

  // ── RCM SERVICES USA ─────────────────────────────────────
  {
    keys: ['rcm usa', 'rcm services usa', 'us rcm', 'revenue cycle usa', 'outsourcing usa', 'us healthcare', 'american medical billing', 'nationwide'],
    answer: `🇺🇸 **RCM Services USA — Market Overview**

**USA market facts:**
• **72%** of US hospitals outsource at least some RCM
• **68%** of physician practices outsource billing
• RCM outsourcing saves **$150K–$400K annually** vs in-house
• Physicians reclaim **15–20 hours/week** previously on billing

**Professional RCM impact vs in-house:**
• First-pass claim acceptance: **95–98%** (vs 78–84% in-house)
• Denial rates reduced: **50–60%**
• Days in A/R: 50–70 days → **25–35 days**
• NLP coding accuracy: **99%+** vs **94%** manual

**Services included in professional RCM USA:**
• Patient Scheduling & Registration
• Real-time eligibility across **5,000+ payers**
• Medical coding: ICD-10, CPT, HCPCS
• Electronic claim submission with pre-scrubbing
• Payment posting & reconciliation
• Denial management (60–70% overturn rate)
• Systematic AR follow-up
• Financial reporting & analytics

**Pricing:** 4–8% of collections (vs 12–18% cost-to-collect in-house)

📖 RCM Services USA → [/healthcare/ai](/healthcare/ai)
📖 Medical Billing Outsourcing → [/medical-billing-outsourcing](/medical-billing-outsourcing)`,
  },

  // ── PATIENT EXPERIENCE ───────────────────────────────────
  {
    keys: ['patient experience', 'patient satisfaction', 'patient billing', 'patient communication', 'patient portal', 'patient payment', 'patient retention'],
    answer: `😊 **Patient Experience & Billing**

**MDeRCM patient-focused automation:**
• Real-time insurance verification **before** appointment — no surprise bills
• Automated explanation of benefits (EOB) processing
• Streamlined patient registration — fast, accurate
• Instant provider alerts for issues
• Automated billing statements with clear language
• Patient payment optimization — multiple payment options
• AI-powered error detection before billing

**Financial impact on patient retention:**
• Better billing experience improves patient retention by **15–25%**
• Worth **$750K–$1M annually** for a $5M practice
• Automated payment plans reduce outstanding balances significantly

📖 AI Patient Intake → [/healthcare/ai/patient-intake](/healthcare/ai/patient-intake)`,
  },

  // ── SPECIALTIES COVERED ──────────────────────────────────
  {
    keys: ['specialties', 'specialty', 'which specialty', 'do you cover', 'cardiology', 'orthopedic', 'pediatrics', 'internal medicine', 'primary care', 'surgery', 'lab', 'diagnostic'],
    answer: `🏥 **Medical Specialties We Serve (25+)**

MDeRCM covers **25+ medical specialties** including:

• 🧠 **Mental Health & Behavioral Health** — LCSW, LPC, psychiatry, psychology
• 💊 **Substance Abuse / SUD** — detox, IOP, PHP, MAT, residential
• 🦷 **Dental** — general dentistry, orthodontics, oral surgery
• 🏥 **Hospital** — inpatient, outpatient, UB-04, DRG
• 👨‍⚕️ **Independent Physicians** — solo to small groups
• 🏢 **Multispecialty Groups** — cross-specialty coordination
• ❤️ **Cardiology**
• 🦴 **Orthopedics**
• 👶 **Pediatrics**
• 🩺 **Primary Care / Internal Medicine**
• 🔬 **Diagnostic Labs**
• 🧬 **Oncology**
• 👁️ **Ophthalmology**
• 🧘 **Physical Therapy / Rehab**
• And many more...

Each specialty gets **specialty-certified coders** for maximum accuracy.

📖 All Services → [/healthcare/ai](/healthcare/ai)
📖 Specialties Overview → [/company](/company)`,
  },

  // ── SECURITY & ISO CERTIFICATIONS ───────────────────────
  {
    keys: ['iso 27001', 'soc 2', 'iso 9001', 'certification', 'certified', 'security standards', 'data protection', 'hipaa security', 'phi protection', 'e-phi', 'employee screening'],
    answer: `🛡️ **MDeRCM Security & Certifications**

**Certifications held:**
• ✅ **ISO 9001:2015** — Quality Management Systems (valid Feb 2027)
• ✅ **ISO 27001:2013** — Information Security Management
• ✅ **SOC 2 Type II** — 5 trust principles, ongoing operational audit
• ✅ **HIPAA** — Privacy & Security Rules

**Technical Safeguards:**
• End-to-End Encryption (at rest & in transit)
• Multi-Factor Authentication
• Intrusion Detection System
• Session Timeout + Password Complexity
• Audit Log Retention
• Disaster Recovery + Regular Data Backup
• PHI, PII, PCI automatic safeguards

**Administrative & Physical:**
• Employee Screening & Access Control
• Comprehensive e-PHI administrative protocols
• Layered physical security systems
• Regular security audits + continuous monitoring

📖 Security & Compliance → [/compliance](/compliance)
📖 Security Section → [/company](/company)`,
  },
];
const DEFAULT_ANSWER = (q) => `Great question! Let me share some relevant resources for **"${q}"**:

**🔥 Most Popular Services:**
• 💲 [Pricing & Free Trial](/pricing) — Performance-based, first 30 days FREE
• 🛡️ [AI Denial Management](/healthcare/ai/denial-management) — 70% denial reduction
• ✅ [Eligibility Verification](/healthcare/ai/eligibility-check) — 8 sec/patient, 600+ payers
• 📋 [Prior Authorization](/healthcare/ai/prior-authorization) — 1.8–2.5 hr turnaround

**🏥 By Practice Type:**
• 👥 [Small Practices](/medical-billing-outsourcing-small-practices) — Save $40K–$60K/year
• 👨‍⚕️ [Independent Physicians](/revenue-cycle-management-independent-physicians) — 25–35% revenue increase
• 🏢 [Multispecialty Clinics](/revenue-cycle-management-multispecialty-practices) — 99%+ clean claim rate
• 🏥 [Hospital RCM](/hospital-revenue-cycle-management) — Trusted by 100+ hospitals

**🤖 All AI Tools:**
• [Payment Posting](/healthcare/ai/payment-posting) • [AR Management](/healthcare/ai/accounts-receivable)
• [Compliance Agent](/healthcare/ai/compliance-agent) • [Patient Intake](/healthcare/ai/patient-intake)
• [Policy Verification](/healthcare/ai/policy-status-verification) • [Contract Repricing](/healthcare/ai/InsuranceContract-Repricing)

**📚 Guides:**
• [Best AI RCM 2026](/blog/best-ai-healthcare-revenue-cycle-management-2026)
• [Mental Health & SUD Billing Guide](/blog/mental-health-substance-abuse-rcm-billing-guide-2026)
• [CO Denial Codes Reference](/resources/denial-codes)

Your details are saved — our expert will personally follow up within **24 hours**! 🎁 [Start Free Trial](/free-trial)`;

// Smart answer function — keyword matching
const getAnswer = (userMsg) => {
  const msg = userMsg.toLowerCase().trim();
  for (const item of KB) {
    if (item.keys.some(k => msg.includes(k))) {
      return item.answer;
    }
  }
  return DEFAULT_ANSWER(userMsg);
};

// ══════════════════════════════════════════════════════════
// CHATBOT COMPONENT

const MDeRCMChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [stage, setStage] = useState('welcome');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [leadData, setLeadData] = useState({ name: '', email: '', phone: '', designation: '' });
  const [currentField, setCurrentField] = useState('name');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const fields = [
    { key: 'name', label: 'May I have your name please? 😊', placeholder: 'e.g. Dr. John Smith', icon: '' },
    { key: 'email', label: 'May I have your email ID? In case our conversation gets disconnected, our team will get back to you 📧', placeholder: 'e.g. john@clinic.com', icon: '' },
    { key: 'phone', label: 'May I have your contact number with country code? 📞', placeholder: 'e.g. +1 (555) 123-4567', icon: '' },
    { key: 'designation', label: 'What is your role / designation? 💼', placeholder: 'e.g. Practice Manager, CFO, Physician', icon: '' },
  ];

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    if (isOpen && stage === 'welcome') {
      setTimeout(() => {
        addBotMessage("👋 Hi! I'm **April**, your AI assistant from MDeRCM!\n\nI can instantly answer questions about:\n• 💲 Pricing & free trial\n• 🤖 AI billing services\n• 🧠 Mental health billing\n• 🛡️ Denial management\n• And much more!\n\nLet's Start! 🌟");
        setStage('collect');
      }, 400);
    }
  }, [isOpen]);

  useEffect(() => {
    if (stage === 'collect' && messages.length > 0) {
      const field = fields.find(f => f.key === currentField);
      if (field) {
        setTimeout(() => addBotMessage(field.label), 300);
      }
    }
  }, [currentField, stage]);

  const addBotMessage = (text) => {
    setMessages(prev => [...prev, { role: 'bot', text, time: new Date() }]);
    if (!isOpen || isMinimized) setUnreadCount(c => c + 1);
  };

  const saveLead = async (data) => {
    try {
      await fetch(SHEETS_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
    } catch (e) { console.error('Lead save error:', e); }
  };

  const simulateTyping = (text, delay) => {
    setIsTyping(true);
    setTimeout(() => { setIsTyping(false); addBotMessage(text); }, delay || 700);
  };

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: trimmed, time: new Date() }]);

    if (stage === 'collect') {
      if (currentField === 'email' && !trimmed.includes('@')) {
        setTimeout(() => addBotMessage('⚠️ Please enter a valid email (e.g. name@clinic.com)'), 300);
        return;
      }
      if (currentField === 'phone' && trimmed.length < 6) {
        setTimeout(() => addBotMessage('⚠️ Please enter a valid contact number.'), 300);
        return;
      }
      const updatedLead = { ...leadData, [currentField]: trimmed };
      setLeadData(updatedLead);
      const fieldIndex = fields.findIndex(f => f.key === currentField);
      if (fieldIndex < fields.length - 1) {
        setCurrentField(fields[fieldIndex + 1].key);
      } else {
        await saveLead(updatedLead);
        setStage('chat');
        setTimeout(() => {
          // ── SMART DESIGNATION-BASED PERSONALIZED SUGGESTIONS ──
          const desig = (updatedLead.designation || '').toLowerCase();
          const nm = updatedLead.name.split(' ')[0]; // First name
          let suggestMsg = '';

          if (desig.includes('physician') || desig.includes('doctor') || desig.includes(' md') || desig.includes(' do') || desig.includes('surgeon') || desig.includes('specialist')) {
            suggestMsg = '👨‍⚕️ **Dr. ' + nm + '**, here\'s what matters most for you:\n\n• [Independent Physician RCM](/revenue-cycle-management-independent-physicians) — Save **10+ hrs/week**, **25–35% revenue increase**\n• [AI Prior Authorization](/healthcare/ai/prior-authorization) — **1.8 hr turnaround**, 93% first-pass approval\n• [Eligibility Verification](/healthcare/ai/eligibility-check) — **8 seconds/patient**, 85% fewer eligibility denials\n• [Pricing](/pricing) — **Performance-based only**, first 30 days FREE\n\n💬 **Ask me anything** — I know your entire website! 👇';
          } else if (desig.includes('cfo') || desig.includes('chief financial') || desig.includes('finance director') || desig.includes('vp finance')) {
            suggestMsg = '💼 **' + nm + '**, as a CFO here\'s the financial picture:\n\n• [Cost Reduction: 40–60% savings](/pricing) — Real ROI numbers\n• [Denial Management](/healthcare/ai/denial-management) — Recover **$45K–$135K annually**\n• [AR Management](/healthcare/ai/accounts-receivable) — **16–24 day A/R** (industry avg: 55–70 days)\n• [15 Benefits of Outsourcing](/benefits-medical-billing-outsourcing-clinics) — Backed by 1,000+ practice data\n• [Cost-Effective RCM Guide](/blog/cost-effective-revenue-cycle-management-reduce-operational-costs)\n\n💬 **Ask me anything** — ROI calculator, pricing, savings estimates! 👇';
          } else if (desig.includes('ceo') || desig.includes('coo') || desig.includes('cmo') || desig.includes('chief executive') || desig.includes('chief operating') || desig.includes('president') || desig.includes('founder') || desig.includes('owner')) {
            suggestMsg = '🏆 **' + nm + '**, as an executive here\'s your strategic overview:\n\n• [Why MDeRCM](/why) — 500+ providers, 99.9% accuracy, 80% task automation\n• [Hospital RCM](/hospital-revenue-cycle-management) — **$15–18M** improved annual performance\n• [Multispecialty RCM](/revenue-cycle-management-multispecialty-practices) — **25–35% revenue increase**\n• [Compliance — ISO 9001, SOC 2, HIPAA](/compliance)\n• [Free RCM Assessment](/free-trial) — Identify **$500K–$2M** in revenue leakage\n\n💬 **Ask me anything** — strategy, ROI, implementation! 👇';
          } else if (desig.includes('administrator') || desig.includes('admin') || desig.includes('director') || desig.includes('health system') || desig.includes('hospital admin')) {
            suggestMsg = '🏥 **' + nm + '**, as a Healthcare Administrator:\n\n• [Hospital RCM](/hospital-revenue-cycle-management) — Trusted by **100+ hospitals**\n• [Multispecialty RCM](/revenue-cycle-management-multispecialty-practices) — Multi-location, 2–3 week setup\n• [Compliance & Security](/compliance) — ISO 9001, ISO 27001, SOC 2, HIPAA\n• [RCM Consulting](/revenue-cycle-consulting) — **35% collection improvement** avg\n• [Events & Company Info](/events)\n\n💬 **Ask me anything** below! 👇';
          } else if (desig.includes('mental') || desig.includes('behavioral') || desig.includes('therapist') || desig.includes('counselor') || desig.includes('psychologist') || desig.includes('lcsw') || desig.includes('lpc') || desig.includes('lmft') || desig.includes('psychiatr') || desig.includes('social worker')) {
            suggestMsg = '🧠 **' + nm + '**, as a Behavioral Health Provider:\n\n• [Mental Health Billing](/MentalHealth) — **95% automation**, MHPAEA/42 CFR Part 2\n• [Mental Health & SUD Guide 2026](/blog/mental-health-substance-abuse-rcm-billing-guide-2026) — Complete billing guide\n• [Best BH RCM Software 2026](/blog/best-behavioral-health-rcm-software-companies-2026) — Comparison vs Valant, Kipu, TherapyNotes\n• [Prior Auth — 80% fewer delays](/healthcare/ai/prior-authorization)\n• Case study: A/R **58 → 19 days**, +**$454K** annual impact\n\n💬 **Ask me anything** — CPT codes, MBHO, parity, telehealth! 👇';
          } else if (desig.includes('dental') || desig.includes('dentist') || desig.includes('orthodon') || desig.includes('oral') || desig.includes('dds') || desig.includes('dmd')) {
            suggestMsg = '🦷 **' + nm + '**, as a Dental Professional:\n\n• [Dental RCM Services](/Dental) — CDT codes, all specialties, 10+ years expertise\n• [Insurance Verification](/healthcare/ai/eligibility-check) — **8 sec/patient**, 30–40% fewer eligibility denials\n• [Dental Denial Management](/healthcare/ai/denial-management) — 70% denial reduction\n• [Dental Automation](/Dental) — Streamlined pre-authorizations\n• ROI: **3–6 months**, 40% cost reduction, 30% revenue increase\n\n💬 **Ask me anything** below! 👇';
          } else if (desig.includes('practice manager') || desig.includes('office manager') || desig.includes('front desk') || desig.includes('coordinator') || desig.includes('billing staff') || desig.includes('billing specialist') || desig.includes('biller')) {
            suggestMsg = '👥 **' + nm + '**, as a Practice Manager/Biller:\n\n• [Small Practice Billing](/medical-billing-outsourcing-small-practices) — Save **$40K–$60K/year**, certified CPC coders\n• [Patient Intake Automation](/healthcare/ai/patient-intake) — Cut front-desk work dramatically\n• [Eligibility Check](/healthcare/ai/eligibility-check) — **8 seconds** vs 15 min manual\n• [Reduce Claim Denials 35–45%](/reduce-claim-denials-healthcare-billing)\n• [CO Denial Codes Reference](/resources/denial-codes) — All 14 common CO codes with fixes\n\n💬 **Ask me anything** — codes, denials, workflow! 👇';
          } else if (desig.includes('rcm') || desig.includes('revenue cycle') || desig.includes('billing manager') || desig.includes('billing director')) {
            suggestMsg = '💡 **' + nm + '**, as an RCM Professional:\n\n• [AI vs Manual RCM](/blog/ai-vs-traditional-rcm) — Full comparison\n• [Best RCM Companies 2026](/blog/best-revenue-cycle-management-companies) — Top 10 criteria\n• [Denial Management Deep Dive](/healthcare/ai/denial-management) — 4-step process\n• [RCM Services USA](/blog/revenue-cycle-management-services-usa) — Market overview\n• [Selecting Best RCM 2026](/blog/selecting-best-rcm-companies-2026)\n\n💬 **Ask me anything** — benchmarks, comparisons, implementation! 👇';
          } else {
            suggestMsg = '🤖 **' + nm + '**, I\'m fully loaded with MDeRCM knowledge!\n\n**Most popular topics:**\n• [💲 Pricing](/pricing) — Performance-based, first 30 days FREE\n• [🛡️ Denial Management](/healthcare/ai/denial-management) — 70% reduction\n• [✅ Eligibility Check](/healthcare/ai/eligibility-check) — 8 sec/patient\n• [🧠 Mental Health Billing](/MentalHealth) — 95% automation\n• [📋 Prior Authorization](/healthcare/ai/prior-authorization) — 1.8 hr turnaround\n• [🏥 Hospital RCM](/hospital-revenue-cycle-management)\n• [📚 All Blogs & Guides](/blog)\n\n💬 **Type any question** below — I have answers for everything! 👇';
          }
          addBotMessage('✅ **Welcome, ' + updatedLead.name + '!** Our expert will follow up at **' + updatedLead.email + '** within **24 hours**.\n\n' + suggestMsg);
        }, 300);
      }
      return;
    }
    simulateTyping(getAnswer(trimmed), 700);
  };

  const renderText = (text) => {
    const lines = text.split('\n');
    return lines.map((line, li) => {
      const boldParts = line.split(/\*\*(.*?)\*\*/g).map((part, i) => {
        if (i % 2 === 1) return <strong key={i}>{part}</strong>;
        const linkParts = [];
        let lLast = 0;
        const lRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        let lMatch;
        while ((lMatch = lRegex.exec(part)) !== null) {
          if (lMatch.index > lLast) linkParts.push(part.slice(lLast, lMatch.index));
          const href = lMatch[2];
          if (href.startsWith('/')) {
            linkParts.push(<Link key={lMatch.index} to={href} style={{ color: '#2E7D32', fontWeight: '600', textDecoration: 'underline' }} onClick={() => setIsOpen(false)}>{lMatch[1]}</Link>);
          } else {
            linkParts.push(<a key={lMatch.index} href={href} target="_blank" rel="noreferrer" style={{ color: '#2E7D32', fontWeight: '600', textDecoration: 'underline' }}>{lMatch[1]}</a>);
          }
          lLast = lMatch.index + lMatch[0].length;
        }
        if (lLast < part.length) linkParts.push(part.slice(lLast));
        return linkParts.length > 0 ? linkParts : part;
      });
      return (
        <span key={li}>
          {boldParts}
          {li < lines.length - 1 && <br />}
        </span>
      );
    });
  };

  const quickReplies = [
    '💲 Pricing & Plans',
    '🎁 Start Free Trial',
    '🛡️ Denial Management',
    '🧠 Mental Health Billing',
    '✅ Eligibility Verification',
    '📋 Prior Authorization',
    '💊 SUD Billing',
    '🦷 Dental RCM',
    '💵 Accounts Receivable',
    '💳 Payment Posting',
    '🏥 Hospital RCM',
    '🤖 AI Services',
    '📊 RCM Consulting',
    '📍 Office Locations',
    '🔒 Compliance & Security',
    '📚 Blog & Guides',
  ];

  const openChat = () => { setIsOpen(true); setIsMinimized(false); setUnreadCount(0); };
  const closeChat = () => { setIsOpen(false); setStage('welcome'); setMessages([]); setLeadData({ name: '', email: '', phone: '', designation: '' }); setCurrentField('name'); };

  return (
    <React.Fragment>

      {/* ── RESPONSIVE CSS ── */}
      <style>{
        "@keyframes tooltip-bounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-5px)} }" +
        "@keyframes pulse-btn { 0%,100%{box-shadow:0 6px 24px rgba(76,175,80,0.5)} 50%{box-shadow:0 6px 32px rgba(76,175,80,0.8),0 0 0 12px rgba(76,175,80,0.12)} }" +
        "@keyframes blink{0%,100%{opacity:1}50%{opacity:0.25}}" +
        "@keyframes bounce-dot{0%,60%,100%{transform:translateY(0)}30%{transform:translateY(-6px)}}" +

        /* Desktop default */
        ".mdercm-win{position:fixed;bottom:24px;right:24px;width:375px;max-width:calc(100vw - 32px);z-index:99999;box-sizing:border-box;}" +
        ".mdercm-fab{position:fixed;bottom:28px;right:28px;z-index:99999;}" +

        /* Tablet 481-768px */
        "@media(max-width:768px){" +
          ".mdercm-win{" +
            "width:calc(100vw - 20px)!important;" +
            "max-width:calc(100vw - 20px)!important;" +
            "right:10px!important;" +
            "left:10px!important;" +
            "bottom:10px!important;" +
            "max-height:78vh!important;" +
            "border-radius:16px!important;" +
            "box-sizing:border-box!important;" +
          "}" +
          ".mdercm-fab{right:14px!important;bottom:14px!important;}" +
        "}" +

        /* Mobile up to 480px — full bottom sheet */
        "@media(max-width:480px){" +
          ".mdercm-win{" +
            "width:100vw!important;" +
            "max-width:100vw!important;" +
            "left:0!important;" +
            "right:0!important;" +
            "bottom:0!important;" +
            "border-radius:20px 20px 0 0!important;" +
            "max-height:85vh!important;" +
            "box-sizing:border-box!important;" +
            "overflow:hidden!important;" +
          "}" +
          ".mdercm-fab{right:16px!important;bottom:16px!important;}" +
          ".mdercm-tooltip{font-size:12px!important;padding:8px 12px!important;max-width:200px!important;}" +
          ".mdercm-fab-btn{width:52px!important;height:52px!important;font-size:22px!important;}" +
        "}" +

        /* Very small mobile 360px and below */
        "@media(max-width:360px){" +
          ".mdercm-win{max-height:88vh!important;}" +
        "}" +

        /* Landscape mobile — side panel */
        "@media(max-width:768px) and (orientation:landscape){" +
          ".mdercm-win{" +
            "width:320px!important;" +
            "max-width:320px!important;" +
            "left:auto!important;" +
            "right:10px!important;" +
            "bottom:10px!important;" +
            "max-height:92vh!important;" +
          "}" +
        "}" +

        /* Touch targets */
        "@media(hover:none) and (pointer:coarse){" +
          ".mdercm-fab-btn{min-width:54px!important;min-height:54px!important;}" +
        "}"
      }</style>

      {/* ── FLOATING BUTTON + TOOLTIP ── */}
      {!isOpen && (
        <div className="mdercm-fab" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>

          {/* Tooltip bubble */}
          <div
            className="mdercm-tooltip"
            onClick={openChat}
            style={{
              position: 'relative',
              background: 'linear-gradient(135deg, #1B5E20, #2E7D32)',
              color: '#fff',
              padding: '10px 16px',
              borderRadius: '20px 20px 4px 20px',
              fontSize: '13.5px',
              fontWeight: '700',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 20px rgba(46,125,50,0.35)',
              border: '2px solid #FF6B35',
              animation: 'tooltip-bounce 2s ease-in-out infinite',
              cursor: 'pointer',
            }}
          >
            💬 How may I help you?
            <div style={{
              position: 'absolute', bottom: '-10px', right: '18px',
              width: 0, height: 0,
              borderLeft: '8px solid transparent',
              borderRight: '8px solid transparent',
              borderTop: '10px solid #FF6B35',
            }} />
          </div>

          {/* Main button */}
          <button
            className="mdercm-fab-btn"
            onClick={openChat}
            style={{
              width: '64px', height: '64px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #2E7D32, #4CAF50)',
              border: '3px solid #FF6B35',
              cursor: 'pointer',
              boxShadow: '0 6px 24px rgba(76,175,80,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '28px', animation: 'pulse-btn 2.5s infinite',
              position: 'relative',
            }}
          >
            💬
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute', top: '-3px', right: '-3px',
                backgroundColor: '#f44336', color: '#fff', borderRadius: '50%',
                width: '22px', height: '22px', fontSize: '11px', fontWeight: '800',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '2px solid #fff',
              }}>{unreadCount}</span>
            )}
          </button>
        </div>
      )}

      {/* ── CHAT WINDOW ── */}
      {isOpen && (
        <div
          className="mdercm-win"
          style={{
            borderRadius: '22px', overflow: 'hidden',
            boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
            fontFamily: "'Segoe UI', system-ui, sans-serif",
            display: 'flex', flexDirection: 'column',
            maxHeight: isMinimized ? '66px' : 'min(590px, 85vh)',
            transition: 'max-height 0.35s cubic-bezier(0.4,0,0.2,1)',
            border: '2px solid #4CAF50',
            boxSizing: 'border-box',
          }}
        >

          {/* HEADER */}
          <div
            onClick={() => setIsMinimized(m => !m)}
            style={{
              background: 'linear-gradient(135deg, #1B5E20, #2E7D32, #4CAF50)',
              padding: '14px 16px', display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', flexShrink: 0, cursor: 'pointer',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '20px', flexShrink: 0,
              }}>
                <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 80 80'%3E%3Ccircle cx='40' cy='40' r='40' fill='%23E8F5E9'/%3E%3C!-- Hair --%3E%3Cellipse cx='40' cy='28' rx='18' ry='22' fill='%234A2C0A'/%3E%3Cellipse cx='40' cy='40' rx='16' ry='10' fill='%234A2C0A'/%3E%3C!-- Face --%3E%3Ccircle cx='40' cy='32' r='15' fill='%23FFCCBC'/%3E%3C!-- Hair top --%3E%3Cellipse cx='40' cy='20' rx='15' ry='10' fill='%234A2C0A'/%3E%3C!-- Eyes --%3E%3Ccircle cx='35' cy='30' r='2' fill='%23333'/%3E%3Ccircle cx='45' cy='30' r='2' fill='%23333'/%3E%3Ccircle cx='36' cy='29' r='0.7' fill='%23fff'/%3E%3Ccircle cx='46' cy='29' r='0.7' fill='%23fff'/%3E%3C!-- Smile --%3E%3Cpath d='M35 36 Q40 41 45 36' stroke='%23FF8A65' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C!-- Cheeks --%3E%3Ccircle cx='32' cy='34' r='3' fill='%23FFAB91' opacity='0.5'/%3E%3Ccircle cx='48' cy='34' r='3' fill='%23FFAB91' opacity='0.5'/%3E%3C!-- Headset --%3E%3Cpath d='M25 28 Q25 15 40 15 Q55 15 55 28' stroke='%231B5E20' stroke-width='2.5' fill='none'/%3E%3Crect x='23' y='27' width='5' height='8' rx='2.5' fill='%232E7D32'/%3E%3Crect x='52' y='27' width='5' height='8' rx='2.5' fill='%232E7D32'/%3E%3Cpath d='M52 33 Q58 36 57 40' stroke='%232E7D32' stroke-width='2' fill='none'/%3E%3Ccircle cx='57' cy='41' r='2' fill='%234CAF50'/%3E%3C!-- Body/Collar --%3E%3Cellipse cx='40' cy='68' rx='20' ry='14' fill='%232E7D32'/%3E%3Cellipse cx='40' cy='55' rx='12' ry='10' fill='%23FFCCBC'/%3E%3C!-- Collar --%3E%3Cpath d='M33 55 L40 62 L47 55' fill='%23fff' opacity='0.9'/%3E%3C/svg%3E" alt="April" style={{ width:'40px',height:'40px',borderRadius:'50%',objectFit:'cover' }} />
              </div>
              <div>
                <div style={{ color: '#fff', fontWeight: '700', fontSize: '14px' }}>April — MDeRCM Assistant</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#69f0ae', animation: 'blink 1.5s infinite' }} />
                  <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '11px' }}>Online • RCM Expert</span>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px' }} onClick={e => e.stopPropagation()}>
              <button
                onClick={() => setIsMinimized(m => !m)}
                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '28px', height: '28px', color: '#fff', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                {isMinimized ? '▲' : '▼'}
              </button>
              <button
                onClick={closeChat}
                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '28px', height: '28px', color: '#fff', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                ✕
              </button>
            </div>
          </div>

          {!isMinimized && (
            <React.Fragment>

              {/* PROGRESS BAR */}
              {stage === 'collect' && (
                <div style={{ height: '3px', backgroundColor: '#e8f5e9', flexShrink: 0 }}>
                  <div style={{
                    height: '100%',
                    width: ((fields.findIndex(f => f.key === currentField) / fields.length) * 100) + '%',
                    background: 'linear-gradient(90deg, #FF6B35, #4CAF50)',
                    transition: 'width 0.4s ease',
                  }} />
                </div>
              )}

              {/* MESSAGES */}
              <div style={{
                flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: '14px 12px',
                backgroundColor: '#F9FBF9', display: 'flex', flexDirection: 'column', gap: '10px',
              }}>
                {messages.map((msg, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                    alignItems: 'flex-end', gap: '7px',
                  }}>
                    {msg.role === 'bot' && (
                      <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 80 80'%3E%3Ccircle cx='40' cy='40' r='40' fill='%23E8F5E9'/%3E%3C!-- Hair --%3E%3Cellipse cx='40' cy='28' rx='18' ry='22' fill='%234A2C0A'/%3E%3Cellipse cx='40' cy='40' rx='16' ry='10' fill='%234A2C0A'/%3E%3C!-- Face --%3E%3Ccircle cx='40' cy='32' r='15' fill='%23FFCCBC'/%3E%3C!-- Hair top --%3E%3Cellipse cx='40' cy='20' rx='15' ry='10' fill='%234A2C0A'/%3E%3C!-- Eyes --%3E%3Ccircle cx='35' cy='30' r='2' fill='%23333'/%3E%3Ccircle cx='45' cy='30' r='2' fill='%23333'/%3E%3Ccircle cx='36' cy='29' r='0.7' fill='%23fff'/%3E%3Ccircle cx='46' cy='29' r='0.7' fill='%23fff'/%3E%3C!-- Smile --%3E%3Cpath d='M35 36 Q40 41 45 36' stroke='%23FF8A65' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C!-- Cheeks --%3E%3Ccircle cx='32' cy='34' r='3' fill='%23FFAB91' opacity='0.5'/%3E%3Ccircle cx='48' cy='34' r='3' fill='%23FFAB91' opacity='0.5'/%3E%3C!-- Headset --%3E%3Cpath d='M25 28 Q25 15 40 15 Q55 15 55 28' stroke='%231B5E20' stroke-width='2.5' fill='none'/%3E%3Crect x='23' y='27' width='5' height='8' rx='2.5' fill='%232E7D32'/%3E%3Crect x='52' y='27' width='5' height='8' rx='2.5' fill='%232E7D32'/%3E%3Cpath d='M52 33 Q58 36 57 40' stroke='%232E7D32' stroke-width='2' fill='none'/%3E%3Ccircle cx='57' cy='41' r='2' fill='%234CAF50'/%3E%3C!-- Body/Collar --%3E%3Cellipse cx='40' cy='68' rx='20' ry='14' fill='%232E7D32'/%3E%3Cellipse cx='40' cy='55' rx='12' ry='10' fill='%23FFCCBC'/%3E%3C!-- Collar --%3E%3Cpath d='M33 55 L40 62 L47 55' fill='%23fff' opacity='0.9'/%3E%3C/svg%3E" alt="April"
                        style={{ width:'28px',height:'28px',borderRadius:'50%',objectFit:'cover',flexShrink:0,border:'1.5px solid #4CAF50' }}
                      />
                    )}
                    <div style={{
                      maxWidth: '82%', padding: '10px 13px',
                      borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '4px 18px 18px 18px',
                      backgroundColor: msg.role === 'user' ? '#FF6B35' : '#fff',
                      color: msg.role === 'user' ? '#fff' : '#1a1a2e',
                      fontSize: '13.5px', lineHeight: '1.65',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                    }}>
                      {renderText(msg.text)}
                      <div style={{ fontSize: '10px', color: msg.role === 'user' ? 'rgba(255,255,255,0.6)' : '#bbb', marginTop: '5px', textAlign: 'right' }}>
                        {msg.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Typing dots */}
                {isTyping && (
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '7px' }}>
                <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 80 80'%3E%3Ccircle cx='40' cy='40' r='40' fill='%23E8F5E9'/%3E%3C!-- Hair --%3E%3Cellipse cx='40' cy='28' rx='18' ry='22' fill='%234A2C0A'/%3E%3Cellipse cx='40' cy='40' rx='16' ry='10' fill='%234A2C0A'/%3E%3C!-- Face --%3E%3Ccircle cx='40' cy='32' r='15' fill='%23FFCCBC'/%3E%3C!-- Hair top --%3E%3Cellipse cx='40' cy='20' rx='15' ry='10' fill='%234A2C0A'/%3E%3C!-- Eyes --%3E%3Ccircle cx='35' cy='30' r='2' fill='%23333'/%3E%3Ccircle cx='45' cy='30' r='2' fill='%23333'/%3E%3Ccircle cx='36' cy='29' r='0.7' fill='%23fff'/%3E%3Ccircle cx='46' cy='29' r='0.7' fill='%23fff'/%3E%3C!-- Smile --%3E%3Cpath d='M35 36 Q40 41 45 36' stroke='%23FF8A65' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C!-- Cheeks --%3E%3Ccircle cx='32' cy='34' r='3' fill='%23FFAB91' opacity='0.5'/%3E%3Ccircle cx='48' cy='34' r='3' fill='%23FFAB91' opacity='0.5'/%3E%3C!-- Headset --%3E%3Cpath d='M25 28 Q25 15 40 15 Q55 15 55 28' stroke='%231B5E20' stroke-width='2.5' fill='none'/%3E%3Crect x='23' y='27' width='5' height='8' rx='2.5' fill='%232E7D32'/%3E%3Crect x='52' y='27' width='5' height='8' rx='2.5' fill='%232E7D32'/%3E%3Cpath d='M52 33 Q58 36 57 40' stroke='%232E7D32' stroke-width='2' fill='none'/%3E%3Ccircle cx='57' cy='41' r='2' fill='%234CAF50'/%3E%3C!-- Body/Collar --%3E%3Cellipse cx='40' cy='68' rx='20' ry='14' fill='%232E7D32'/%3E%3Cellipse cx='40' cy='55' rx='12' ry='10' fill='%23FFCCBC'/%3E%3C!-- Collar --%3E%3Cpath d='M33 55 L40 62 L47 55' fill='%23fff' opacity='0.9'/%3E%3C/svg%3E" alt="April" style={{ width:'28px',height:'28px',borderRadius:'50%',objectFit:'cover',border:'1.5px solid #4CAF50',flexShrink:0 }} />
                    <div style={{ backgroundColor: '#fff', borderRadius: '4px 18px 18px 18px', padding: '12px 16px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        {[0, 1, 2].map(i => (
                          <div key={i} style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#4CAF50', animation: 'bounce-dot 1.2s ' + (i * 0.2) + 's infinite' }} />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* QUICK REPLIES — DROPDOWN */}
              {stage === 'chat' && !isTyping && (
                <div style={{ padding: '6px 10px', backgroundColor: '#F1F8E9', borderTop: '1px solid #C8E6C9', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '11px', color: '#4CAF50', fontWeight: '700', whiteSpace: 'nowrap' }}>💬 Quick Ask:</span>
                  <select
                    onChange={e => {
                      const val = e.target.value;
                      if (!val) return;
                      const clean = val.replace(/^[^\s]+\s/, '');
                      setMessages(prev => [...prev, { role: 'user', text: val, time: new Date() }]);
                      simulateTyping(getAnswer(clean), 600);
                      e.target.value = '';
                    }}
                    defaultValue=""
                    style={{
                      flex: 1,
                      padding: '5px 8px',
                      border: '1.5px solid #4CAF50',
                      borderRadius: '10px',
                      backgroundColor: '#fff',
                      color: '#2E7D32',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      outline: 'none',
                      fontFamily: 'inherit',
                    }}
                  >
                    <option value="" disabled>Select a topic to ask...</option>
                    {quickReplies.map((q, i) => (
                      <option key={i} value={q}>{q}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* INPUT */}
              <div style={{ padding: '10px 12px', backgroundColor: '#fff', borderTop: '1px solid #e8f5e9', display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                <input
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  placeholder={
                    stage === 'collect'
                      ? (fields.find(f => f.key === currentField)?.icon || '') + ' Enter ' + (fields.find(f => f.key === currentField)?.label || '') + '...'
                      : 'Ask about pricing, services, billing...'
                  }
                  style={{
                    flex: 1, padding: '10px 14px', borderRadius: '22px',
                    border: '1.5px solid #A5D6A7', outline: 'none',
                    fontSize: '13.5px', backgroundColor: '#f9fbf9', color: '#222',
                  }}
                  onFocus={e => e.target.style.borderColor = '#4CAF50'}
                  onBlur={e => e.target.style.borderColor = '#A5D6A7'}
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || isTyping}
                  style={{
                    width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0,
                    background: input.trim() ? 'linear-gradient(135deg, #FF6B35, #e65100)' : '#ddd',
                    border: 'none', cursor: input.trim() ? 'pointer' : 'not-allowed',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '16px', color: '#fff', transition: 'background 0.2s',
                  }}
                >
                  ➤
                </button>
              </div>

              {/* FOOTER */}
              <div style={{ backgroundColor: '#fff', padding: '5px', textAlign: 'center', borderTop: '1px solid #f1f8e9', flexShrink: 0 }}>
                <span style={{ fontSize: '10px', color: '#bbb' }}>Powered by <strong style={{ color: '#2E7D32' }}>MDeRCM</strong> • HIPAA Compliant</span>
              </div>

            </React.Fragment>
          )}
        </div>
      )}

    </React.Fragment>
  );
};

export default MDeRCMChatbot;